import { useState } from 'react'
import { X } from 'lucide-react'
import { fieldLabel, fieldWrapper, selectBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { applySheet } from '../api/calculation-sheet.api'
import type { CalcPosition } from '../types/calculation.types'

/**
 * AP-37 15 E — **„Blatt auf Markierte anwenden"**: Das Blatt einer Position
 * (Material, Arbeitsschritte, wahlweise abweichende Aufschläge) ersetzt die
 * Blätter der markierten Positionen. Mengen je Einheit bleiben je Einheit —
 * die Positionsmenge des Ziels gilt.
 *
 * Ein Vorgang als Einheit mit dem Verb als Knopf (AP-36).
 */
export function BlattAnwendenDialog({
  kalkulationId,
  positionen,
  markiert,
  onClose,
  onDone,
}: {
  kalkulationId: string
  positionen: CalcPosition[]
  markiert: ReadonlySet<string>
  onClose: () => void
  onDone: (angewendet: number) => void
}) {
  const quellen = positionen.filter((p) => p.kind !== 'TEXT' && (p.costLineCount > 0 || p.workStepCount > 0))
  const [quelle, setQuelle] = useState(quellen.find((p) => markiert.has(p.id))?.id ?? quellen[0]?.id ?? '')
  const [aufschlaege, setAufschlaege] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const ziele = positionen.filter((p) => markiert.has(p.id) && p.id !== quelle && p.kind !== 'TEXT')
  const ersetzt = ziele.filter((p) => p.costLineCount > 0 || p.workStepCount > 0).length

  const anwenden = async () => {
    try {
      const r = await applySheet(kalkulationId, { fromPositionId: quelle, toPositionIds: ziele.map((p) => p.id), surcharges: aufschlaege })
      onDone(r.applied)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Blatt ließ sich nicht anwenden.'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">Blatt auf Markierte anwenden</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
        {quellen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Position hat ein Blatt, das sich anwenden ließe.</p>
        ) : (
          <div className="space-y-4">
            <div className={fieldWrapper}>
              <label className={fieldLabel}>Blatt von Position</label>
              <select className={selectBase} value={quelle} onChange={(e) => setQuelle(e.target.value)}>
                {quellen.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayNumber} {p.shortText} — {p.costLineCount} Kostenzeile(n), {p.workStepCount} Schritt(e)
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={aufschlaege} onChange={(e) => setAufschlaege(e.target.checked)} />
              abweichende Aufschläge mitnehmen
            </label>
            <p className="text-sm">
              Auf {ziele.length} Position(en):{' '}
              <span className="text-muted-foreground">
                {ziele
                  .slice(0, 6)
                  .map((p) => p.displayNumber)
                  .join(', ')}
                {ziele.length > 6 ? ' …' : ''}
              </span>
            </p>
            {ersetzt > 0 && (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-foreground">
                {ersetzt} davon haben schon ein Blatt — es wird ersetzt.
              </p>
            )}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
            disabled={!quelle || ziele.length === 0}
            onClick={() => void anwenden()}
          >
            Anwenden
          </button>
        </div>
      </div>
    </div>
  )
}
